import { inject } from '@angular/core';
import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { from, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { APP_SETTINGS } from './runtime-settings';
import { AuthService } from './auth.service';
import { ErrorService } from './error.service';

/** When set, 401 will trigger logout only (no refresh retry). Used for the retry request. */
export const SKIP_REFRESH_RETRY = new HttpContextToken<boolean>(() => false);

function messageFromHttpError(err: any): string {
  return err?.error?.message ?? err?.message ?? err?.statusText ?? `Error ${err?.status ?? 'unknown'}`;
}

function isCheckTokenValidityRequest(url: string | undefined): boolean {
  if (!url) return false;
  return url.indexOf('/rpc/check_token_validity') > -1;
}

function isLoginRequest(url: string | undefined): boolean {
  if (!url) return false;
  return url.indexOf('/rpc/auth/login') > -1;
}

function isLogoutRequest(url: string | undefined): boolean {
  if (!url) return false;
  return url.indexOf('/rpc/auth/logout') > -1;
}

function isRefreshTokenRequest(url: string | undefined, apiBaseUrl: string): boolean {
  if (!url) return false;
  return url === apiBaseUrl + '/rpc/auth/refresh_token' || url?.endsWith('/rpc/auth/refresh_token');
}

// Functional HTTP interceptor for handling HTTP errors consistently
export const errorInterceptor: HttpInterceptorFn = (request, next) => {
    const { apiBaseUrl } = inject(APP_SETTINGS);
    const authService = inject(AuthService);
    const errorService = inject(ErrorService);

    return next(request).pipe(
        catchError((err: any) => {
            const url = err?.url ?? request.url;
            // ignore errors that occur while error logging
            if (url === apiBaseUrl + '/rpc/log_error' || url?.endsWith('/rpc/log_error')) {
                return of(null as any);
            }

            // Handle authentication errors (401/403)
            if (err?.status === 401 || err?.status === 403) {
                if (isLoginRequest(url)) {
                    authService.removeToken();
                    authService.logout();
                    return throwError(() => err);
                }
                // ignore errors from logout request, this is an extreme case and we should just remove tokens and continue on this case
                if (isLogoutRequest(url)) {
                    return of(null as any);
                }
                // Already retried after refresh; do not try again
                if (request.context.get(SKIP_REFRESH_RETRY)) {
                    authService.logout();
                    return of(null as any);
                }
                // Refresh endpoint failed; do not try to refresh again
                if (isRefreshTokenRequest(url, apiBaseUrl)) {
                    authService.logout();
                    return of(null as any);
                }
                // Only attempt refresh for requests to apiBaseUrl when we have a token
                if (request.url.indexOf(apiBaseUrl) === 0) {
                    return from(authService.getToken()).pipe(
                        switchMap((token) => {
                            if (!token) {
                                authService.logout();
                                return of(null as any);
                            }
                            return from(authService.refreshTokenOnce(isCheckTokenValidityRequest(url))).pipe(
                                switchMap((success) => {
                                    if (success) {
                                        if (isCheckTokenValidityRequest(url)) {
                                            return of(true as any);
                                        }
                                        // Retry bypasses token interceptor, so attach the new token ourselves
                                        return from(authService.getToken()).pipe(
                                            switchMap((newToken) => {
                                                if (!newToken) {
                                                    authService.logout();
                                                    return of(null as any);
                                                }
                                                const retryReq = request.clone({
                                                    context: request.context.set(SKIP_REFRESH_RETRY, true),
                                                    setHeaders: { Authorization: `Bearer ${newToken}` },
                                                });
                                                return next(retryReq);
                                            })
                                        );
                                    }
                                    authService.logout();
                                    return of(null as any);
                                }),
                                catchError(() => {
                                    authService.logout();
                                    return of(null as any);
                                })
                            );
                        })
                    );
                }
                // Other 401/403 (e.g. not apiBaseUrl): logout
                authService.logout();
                return of(null as any);
            }
            // Handle other HTTP errors: report to ErrorService (except login), then rethrow
            /* if (!isLoginRequest(url)) {
                errorService.report({
                    message: messageFromHttpError(err),
                    code: err?.status != null ? String(err.status) : undefined,
                });
            }*/
            return throwError(() => err);
        })
    );
};
